import { useMemo } from 'react';
import { connect } from 'react-redux';
import { useMaterialReactTable, MaterialReactTable } from 'material-react-table';
import { useNavigate } from 'react-router-dom';
import { IconButton } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { unique } from '../../utils/collections';
import EquipmentLoader from '../loaders/EquipmentLoader';
import { getBuildingsEntities } from '../../redux/selectors/buildings';
import { defaultTableOptions, defaultTableInitialState } from '../util/DataTable';

const PointsTable = ({
  points,
  buildingsById,
  orgsById,
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  equipmentSelection,
  pointSelection,
  onRowSelectionChange,
  pointTypesById,
  unitsById,
}) => {
  const navigate = useNavigate();
  const data = useMemo(() => {
    const eids = Object.entries(equipmentSelection)
      // eslint-disable-next-line no-unused-vars
      .filter(([k, v]) => v)
      // eslint-disable-next-line no-unused-vars
      .map(([k, v]) => parseInt(k, 10));
    let ps = points.values();
    if (eids.length !== 0) ps = ps.filter((p) => p.equipIds.some((eid) => eids.includes(eid)));
    return ps
      .map((p) => ({
        building: buildingsById[p.buildingId],
        org: orgsById[buildingsById[p.buildingId].orgId],
        ...p,
      }))
      .toArray();
  }, [orgsById, points, buildingsById, equipmentSelection]);

  const observedEquipmentNames = useMemo(
    () => unique(data.flatMap(({ equipment }) => equipment.map((e) => e.name))),
    [data]
  );
  const observedPointTypeIds = useMemo(
    () => unique(data.map(({ pointTypeId }) => pointTypeId)),
    [data]
  );
  const observedUnitIds = useMemo(() => unique(data.map(({ rawUnitId }) => rawUnitId)), [data]);

  const columns = useMemo(() => {
    const orgsOptions = Object.values(orgsById).map((o) => ({ label: o.name, value: o.id }));
    const pointTypeOptions = Object.entries(pointTypesById)
      .map(([value, pt]) => ({
        label: pt.name,
        value: Number.parseInt(value, 10),
      }))
      .filter(({ value }) => observedPointTypeIds.includes(value));
    const unitOptions = Object.entries(unitsById)
      .map(([value, unit]) => ({
        label: unit.name_abbr,
        value: Number.parseInt(value, 10),
      }))
      .filter(({ value }) => observedUnitIds.includes(value));
    const pointType = ({ row, renderedCellValue }) => (
      <>
        {pointTypesById[renderedCellValue]?.name ?? renderedCellValue}
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/staging?view=Point&bid=${row.original.buildingId}`);
          }}
        >
          <Edit />
        </IconButton>
      </>
    );
    return [
      // TODO: group shared column defs in shared file
      {
        id: 'o_name',
        accessorFn: (b) => [b.org.id],
        header: 'Organization Name',
        filterVariant: 'multi-select',
        filterSelectOptions: orgsOptions,
        Cell: ({ renderedCellValue }) => orgsById[renderedCellValue]?.name ?? renderedCellValue,
      },
      {
        id: 'b_name',
        header: 'Building Name',
        accessorFn: ({ building }) => building?.name,
        filterVariant: 'autocomplete',
      },
      {
        id: 'p_e_names',
        header: 'Equipment Names',
        accessorFn: (p) => p?.equipment?.map((e) => e.name),
        filterVariant: 'multi-select',
        filterSelectOptions: observedEquipmentNames,
      },
      { id: 'p_name', accessorKey: 'name', header: 'Point Name', filterVariant: 'autocomplete' },
      {
        id: 'p_type',
        accessorFn: ({ pointTypeId }) => [pointTypeId],
        header: 'Point Type',
        filterVariant: 'multi-select',
        filterSelectOptions: pointTypeOptions,
        Cell: pointType,
      },
      { id: 'p_value', accessorFn: ({ value }) => value || 'n/a', header: 'Last Known Value' },
      {
        id: 'p_units',
        accessorFn: ({ rawUnitId }) => [rawUnitId],
        header: 'Units',
        filterVariant: 'multi-select',
        filterSelectOptions: unitOptions,
        Cell: ({ renderedCellValue }) =>
          unitsById[renderedCellValue]?.name_abbr ?? renderedCellValue,
      },
      {
        id: 'p_lastIngest',
        header: 'Last Data Ingested',
        sortingFn: 'datetime',
        enableColumnFilter: false,
        accessorFn: ({ lastUpdatedTs }) => new Date(lastUpdatedTs || 0),
        Cell: ({ renderedCellValue }) => {
          if (renderedCellValue.valueOf() === 0) {
            return 'Never';
          }
          return renderedCellValue.toLocaleString();
        },
      },
      {
        id: 'p_object_id',
        header: 'Object Id',
        accessorFn: ({ objectId }) => objectId,
      },
      {
        id: 'p_device_id',
        header: 'Device Id',
        accessorFn: ({ device }) => device?.id ?? 'UNKNOWN',
      },
      {
        id: 'p_device_name',
        header: 'Device Name',
        accessorFn: ({ device }) => device?.name ?? 'UNKNOWN',
      },
      {
        id: 'p_tags',
        header: 'Haystack Tags',
        accessorFn: ({ tags }) => (tags?.toSorted() ?? []).join(', '),
      },
      {
        id: 'p_labels',
        header: 'Custom Labels',
        accessorFn: ({ labels }) => (labels?.toSorted() ?? []).join(', '),
      },
    ];
  }, [
    orgsById,
    pointTypesById,
    unitsById,
    navigate,
    observedEquipmentNames,
    observedPointTypeIds,
    observedUnitIds,
  ]);

  const showBuildings = Object.keys(buildingsById).length > 1;
  const showOrgs =
    showBuildings && unique(Object.values(buildingsById).map((b) => b.orgId)).length <= 1;

  const columnIds = useMemo(() => columns.map(({ id }) => id), [columns]);
  const pointSorting = useMemo(
    () => sorting.filter(({ id }) => columnIds.includes(id)),
    [sorting, columnIds]
  );
  const pointColumnFilters = useMemo(
    () => columnFilters.filter(({ id }) => columnIds.includes(id)),
    [columnFilters, columnIds]
  );

  const mrt = useMaterialReactTable({
    ...defaultTableOptions(true),
    columns,
    data,
    initialState: {
      ...defaultTableInitialState(pointColumnFilters, pointSorting),
      columnVisibility: {
        o_name: showOrgs,
        b_name: showBuildings,
        p_device_id: false,
        p_device_name: false,
        p_object_id: false,
        p_tags: false,
        p_labels: false,
      },
    },
    onRowSelectionChange,
    // sorting
    onSortingChange,
    // filtering
    onColumnFiltersChange,
    state: {
      sorting: pointSorting,
      columnFilters: pointColumnFilters,
      rowSelection: pointSelection,
    },
  });

  if (Object.keys(buildingsById).length <= 0) return 'Too few buildings';
  if (Object.keys(buildingsById).length >= 10) return 'Too many buildings';
  return (
    <div>
      {Object.keys(buildingsById).map((bid) => (
        // EquipmentLoader also loads points
        <EquipmentLoader key={bid} buildingId={bid} getDevices />
      ))}
      <MaterialReactTable table={mrt} />
    </div>
  );
};

const mapStateToProps = (state, { buildingsById, orgsById }) => ({
  ...getBuildingsEntities(state, Object.keys(buildingsById)),
  buildingsById,
  orgsById,
  pointTypesById: state.entities.pointTypes.byId || {},
  unitsById: state.entities.units.byId || {},
});

// eslint-disable-next-line no-unused-vars
const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(PointsTable);
